
import { motion } from 'framer-motion';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { YoungSchemasResult } from '@/types/personality';
import { getSchemaDescriptions } from '@/utils/youngSchemasScoring';

interface YoungSchemasResultsChartProps {
  results: YoungSchemasResult;
}

export default function YoungSchemasResultsChart({ results }: YoungSchemasResultsChartProps) {
  // Preparar dados para o gráfico
  const chartData = Object.entries(results.scores)
    .map(([schema, score]) => {
      const { title } = getSchemaDescriptions(schema);
      return {
        name: schema,
        title: title,
        score: score,
        // Cores diferentes baseadas no valor
        color: score >= 5 ? '#ef4444' : // vermelho para alto
               score >= 3.5 ? '#f59e0b' : // âmbar para médio
               '#10b981' // verde para baixo
      };
    })
    .sort((a, b) => b.score - a.score); // Ordenar por pontuação

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border shadow-md rounded-md">
          <p className="font-medium">{payload[0].payload.title}</p>
          <p>Pontuação: <span className="font-medium">{payload[0].value.toFixed(1)}</span></p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="h-[400px] w-full"
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart 
          data={chartData}
          margin={{
            top: 10,
            right: 10,
            left: 10,
            bottom: 50
          }}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            type="number" 
            domain={[0, 6]} 
            tickCount={7}
          />
          <YAxis 
            type="category" 
            dataKey="title" 
            width={150}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar 
            dataKey="score" 
            fill="#8884d8" 
            barSize={20}
            label={{ position: 'right', formatter: (value: number) => value.toFixed(1) }}
            isAnimationActive={true}
          >
            {chartData.map((entry, index) => (
              <motion.rect 
                key={`bar-${index}`}
                fill={entry.color}
                initial={{ width: 0 }}
                animate={{ width: 'auto' }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </motion.div>
  );
}
